import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminSidebar } from '../../components/Sidebar';
import { Swords } from 'lucide-react';
import api from '../../utils/api';

const STATUS_INFO = {
  draft:     { cls: 'badge-gray',   label: 'Entwurf' },
  scheduled: { cls: 'badge-blue',   label: 'Geplant' },
  lobby:     { cls: 'badge-orange', label: 'Lobby' },
  active:    { cls: 'badge-green',  label: 'Aktiv' },
  finished:  { cls: 'badge-gray',   label: 'Beendet' },
};

const DUEL_STATUS = {
  draft:     { cls: 'badge-gray',   label: 'Bereit' },
  scheduled: { cls: 'badge-blue',   label: 'Geplant' },
  lobby:     { cls: 'badge-orange', label: 'Lobby offen' },
  active:    { cls: 'badge-green',  label: 'Läuft' },
};

export default function AdminQuizzes() {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duelInvitations, setDuelInvitations] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/quizzes').then(r => setQuizzes(r.data.quizzes)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const fetchInvitations = () =>
      api.get('/admin/quizzes/duel-invitations').then(r => setDuelInvitations(r.data.invitations || [])).catch(() => {});
    fetchInvitations();
    const interval = setInterval(fetchInvitations, 5000);
    return () => clearInterval(interval);
  }, []);

  const startLobby = async (quizId) => {
    try {
      const r = await api.post(`/admin/quizzes/${quizId}/start`);
      navigate(`/admin/quizzes/${quizId}/lobby`, { state: { ...r.data } });
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Starten');
    }
  };

  const duplicate = async (quizId) => {
    const r = await api.post(`/admin/quizzes/${quizId}/duplicate`);
    setQuizzes(p => [r.data.quiz, ...p]);
  };

  const endQuiz = async (quizId) => {
    if (!window.confirm('Quiz wirklich beenden? Die laufende Session wird geschlossen.')) return;
    try {
      await api.post(`/admin/quizzes/${quizId}/end`);
      setQuizzes(p => p.map(q => q.id === quizId ? { ...q, status: 'finished' } : q));
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Beenden');
    }
  };

  const deleteQuiz = async (quizId) => {
    if (!window.confirm('Quiz wirklich löschen? Alle Spieldaten werden dauerhaft entfernt.')) return;
    try {
      await api.delete(`/admin/quizzes/${quizId}`);
      setQuizzes(p => p.filter(q => q.id !== quizId));
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <AdminSidebar />
      <main className="main-content">
        <div className="flex justify-between items-center mb-6">
          <h1 style={{ fontSize: 28 }}>Quiz-Verwaltung</h1>
          <Link to="/admin/quizzes/new" className="btn btn-primary">Quiz erstellen</Link>
        </div>

        {duelInvitations.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Swords size={15} /> Duell-Einladungen
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {duelInvitations.map(inv => {
                const st = DUEL_STATUS[inv.status] || { cls: 'badge-gray', label: inv.status };
                const canJoin = inv.status === 'lobby' || inv.status === 'active';
                return (
                  <div key={inv.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', borderLeft: '4px solid #8B6A1C', background: '#FFFBF0' }}>
                    {inv.host_logo
                      ? <img src={inv.host_logo} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'contain', background: '#fff', border: '1px solid var(--border)', flexShrink: 0 }} />
                      : <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>{inv.host_mosque_name?.[0] || '?'}</div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>{inv.title}</span>
                        <span className={`badge ${st.cls}`}>{st.label}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 3 }}>
                        Eingeladen von <strong>{inv.host_mosque_name}</strong>
                      </div>
                      {canJoin && inv.pin && (
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: '#8B6A1C', letterSpacing: 4 }}>{inv.pin}</span>
                          <span style={{ fontSize: 12, color: 'var(--text3)' }}>PIN</span>
                        </div>
                      )}
                      {!canJoin && (
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                          Wartet auf Start durch {inv.host_mosque_name}
                        </div>
                      )}
                    </div>
                    {canJoin && inv.pin && (
                      <button
                        className="btn btn-accent btn-sm"
                        onClick={() => navigate(`/guest/${inv.pin}`)}
                        style={{ flexShrink: 0 }}
                      >
                        Lobby öffnen
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? <div className="spinner-wrap"><div className="spinner" /></div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {quizzes.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: 48 }}>
                <p style={{ color: 'var(--text3)', marginBottom: 20 }}>Noch kein Quiz erstellt</p>
                <Link to="/admin/quizzes/new" className="btn btn-primary">Erstes Quiz erstellen</Link>
              </div>
            ) : quizzes.map(q => {
              const si = STATUS_INFO[q.status] || { cls: 'badge-gray', label: q.status };
              return (
                <div key={q.id} className="card" style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 16 }}>{q.title}</span>
                      <span className={`badge ${si.cls}`}>{si.label}</span>
                    </div>
                    <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>
                      {q.question_count} Fragen · {q.session_count} Spiele
                      {q.last_played && ` · Zuletzt: ${new Date(q.last_played).toLocaleDateString('de-CH')}`}
                    </div>
                    {q.pin && (
                      <div style={{ marginTop: 6 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: 'var(--primary)', letterSpacing: 4 }}>
                          {q.pin}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 8 }}>PIN</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Link to={`/admin/quizzes/${q.id}`} className="btn btn-sm btn-ghost">Bearbeiten</Link>
                    <Link to={`/admin/quizzes/${q.id}/sessions`} className="btn btn-sm btn-ghost">Ergebnisse</Link>
                    {(q.status === 'draft' || q.status === 'scheduled') && (
                      <button className="btn btn-sm btn-primary" onClick={() => startLobby(q.id)}>Starten</button>
                    )}
                    {q.status === 'lobby' && (
                      <button className="btn btn-sm btn-accent" onClick={() => startLobby(q.id)}>Lobby</button>
                    )}
                    {(q.status === 'lobby' || q.status === 'active') && (
                      <button className="btn btn-sm btn-ghost" style={{ color: 'var(--error)' }} onClick={() => endQuiz(q.id)}>Beenden</button>
                    )}
                    <button className="btn btn-sm btn-ghost" onClick={() => duplicate(q.id)} title="Duplizieren">Kopie</button>
                    <button className="btn btn-sm btn-ghost" style={{ color: 'var(--error)' }} onClick={() => deleteQuiz(q.id)}>Löschen</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
